import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import SiteShell from "@/components/SiteShell";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft,
  ArrowRight,
  HelpCircle,
  ThumbsUp,
  ThumbsDown,
  Check,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "af_faq_votes_v1";

const FAQS = [
  {
    q: "Is this 90 days or 3 months?",
    a: "Think of it as either 90 days or 3 months, whichever framing fits your campaign best. A single-weekend festival or a tight seasonal push might play out in a shorter window. A growing home services brand might need the monthly consistency of all three months to build momentum. We'll shape the calendar around your goal, not the other way around. Either way, $4,500 is our minimum starting point so the campaign has enough runway to gather real performance data and prove the playbook in your market.",
  },
  {
    q: "Can I pay monthly instead of paying $4,500 upfront?",
    a: "Yes. Every $4,500 package can be paid as 3 monthly invoices of $1,500, or as a single upfront $4,500 payment. Whichever fits your cash flow and your campaign schedule best.",
  },
  {
    q: "What is your minimum advertising package?",
    a: "$4,500 total (or $1,500/month for 3 months). That covers ad spend, creative, weekly check-ins, monthly reporting, and the strategy work to launch the campaign.",
  },
  {
    q: "What's included in every package?",
    a: "Strategy, audience targeting, creative concepting, ad placement on the channels in your tier, weekly performance check-ins, and a monthly report you can take to your board, partner, or lender. Creative refreshes are included where noted on the tier.",
  },
  {
    q: "Do you create the ads, or do I bring my own creative?",
    a: "Both. Every package includes one bonus creative set for your PMAX campaign and one bonus Meta carousel, designed by us. If your campaign needs a new video shoot or your messaging and imagery need to change mid-flight, that's priced separately and we'll quote it before any work starts. Ask us for the creative add-on rate sheet and we'll send it over.",
  },
  {
    q: "What can I expect to see after the first 90 days?",
    a: "For tourism brands: website visits, booking activity, foot-traffic signals, and channel-level performance. For home services: calls booked, lead-form fills, and cost-per-booked-job. Either way you'll have a board-ready data picture and a clear recommendation on what to do next.",
  },
  {
    q: "Can I see sample data from each strategy?",
    a: "Yes. Sample 90-day reports for each strategy (TV Broadcast, Desktop & Mobile Ads, Social Media, and Omni-Channel) are coming to this page soon, with the actual reach, engagement, and conversion ranges we've delivered in each tier. In the meantime, ask us on your strategy call and we'll walk you through the most relevant case for your market.",
  },
  {
    q: "How do you report performance?",
    a: "Weekly check-ins by phone or email and a monthly written report. The monthly is built to be readable by a non-marketer, your board, your partner, your county leadership. Brand reach, engagement, year-over-year delta, and a plain-language summary of what's working and what's changing next.",
  },
  {
    q: "Do you work with VTC grant recipients?",
    a: "Yes. Our tourism work is built to satisfy Virginia Tourism Corporation reporting requirements, including the Marketing Leverage Program (MLP) and ARPA tourism funds. Creative is VIFL-compliant where required so the spend ties cleanly to the grant at audit time.",
  },
  {
    q: "What's the difference between the four package tiers?",
    a: "TV Broadcast ($4,500) is reach-first, streaming + premium video inventory. Desktop & Mobile Ads ($4,500) is action-first via PMAX across all of Google. Social Media ($4,500) is paid social only, Meta-focused. Omni-Channel ($9,000) combines Streaming TV and PMAX across multiple DMAs for full-funnel campaigns.",
  },
  {
    q: "Can I switch tiers mid-campaign?",
    a: "No. Every strategy has a 3-month minimum so we can run it long enough to gather real performance data. At the 90-day review we'll recommend any tier or budget shift for the next chapter based on what the numbers are telling us.",
  },
  {
    q: "What happens after 90 days? Do I have to renew?",
    a: "Renewal is up to you. After 90 days we'll review performance together and present a recommendation. If we've earned it, we extend the relationship from a place of evidence. If we haven't, you keep the creative and the learnings, no questions asked.",
  },
  {
    q: "What industries do you work in?",
    a: "Tourism (DMOs, wineries, breweries, cideries, distilleries, hotels, attractions, restaurants, festivals) and Central Virginia home services (HVAC, plumbing, electrical, roofing, lawn & landscape, and other trusted trades).",
  },
  {
    q: "What about Search, SEO, Google Ads, or Pay-Per-Click?",
    a: "All of those services are available through us, just send a note and we'll talk through it. We don't lead with them in these packages because search is a passive medium: a customer has to already be thinking about you to type your category into Google. The packages on this page are built around intrusive media (streaming TV, mobile geo-targeting, paid social) where we put your brand in front of people who weren't actively shopping yet. Intrusive media creates the demand. Search captures it. Most clients do best when intrusive runs first and search is layered in once the awareness curve starts to bend, but we're happy to add Search, SEO, Google Ads, or PPC to any campaign if that's the right next move for your business.",
  },
  {
    q: "Who creates the ads, and how much does that cost?",
    a: "We can handle any kind of creative an ad campaign needs — the price depends on the quality and complexity. We work with video production companies, graphic designers, illustrators, AI agent developers, drone operators, Reels editors, and more every day. Every campaign starts with one creative bundle included: a set of display creatives in multiple sizes for your PMAX campaign, plus 6–10 1080×1080 carousel images for Meta. After that we'll price out exactly what your campaign needs, or revise the first set based on what's working. If you already have finished art or finished video files, even better — we'll plug them straight in. We're flexible: we collaborate closely with many design teams and meet you wherever your creative is.",
  },
  {
    q: "Where are you based and who will I work with?",
    a: "Charlottesville, Virginia. You'll work directly with the principals, Anne Flinn and Collean Laney, every week. No account-rep shuffle.",
  },
];

const FaqFeedbackButtons = ({ faqId, question, voted, onVote }) => {
  if (voted) {
    return (
      <div
        className="mt-4 pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-[var(--af-lime)]"
        data-testid={`faq-vote-thanks-${faqId}`}
      >
        <Check size={14} />
        <span>Thanks — your feedback helps us write sharper answers.</span>
      </div>
    );
  }
  return (
    <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-3">
      <span className="text-xs uppercase tracking-widest text-[var(--af-cream)]/55">
        Was this helpful?
      </span>
      <button
        type="button"
        onClick={() => onVote("up")}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 hover:border-[var(--af-lime)] hover:text-[var(--af-lime)] transition-colors text-[var(--af-cream)]/85"
        data-testid={`faq-vote-up-${faqId}`}
        aria-label="Yes, this was helpful"
      >
        <ThumbsUp size={13} /> Yes
      </button>
      <button
        type="button"
        onClick={() => onVote("down")}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 hover:border-[var(--af-lime)] hover:text-[var(--af-lime)] transition-colors text-[var(--af-cream)]/85"
        data-testid={`faq-vote-down-${faqId}`}
        aria-label="No, this was not helpful"
      >
        <ThumbsDown size={13} /> No
      </button>
    </div>
  );
};

const PackagesFAQ = () => {
  const [votes, setVotes] = useState({});

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setVotes(JSON.parse(stored));
    } catch (_) {
      /* ignore corrupted storage */
    }
  }, []);

  const handleVote = useCallback(
    async (faqId, question, vote) => {
      if (votes[faqId]) return; // single vote per FAQ per browser
      const next = { ...votes, [faqId]: vote };
      setVotes(next);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (_) {
        /* ignore quota errors */
      }
      try {
        await fetch(`${API}/api/faq-feedback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            faq_id: faqId,
            question,
            vote,
            source_page: "/packages/faq",
          }),
        });
      } catch (err) {
        // Non-blocking — vote stays recorded locally even if network blips.
        console.warn("FAQ feedback post failed:", err);
      }
    },
    [votes]
  );

  return (
    <SiteShell active="packages">
      <section
        className="grid-bg px-5 sm:px-8 py-20 sm:py-28"
        data-testid="faq-hero"
      >
        <div className="max-w-4xl mx-auto">
          <Link
            to="/#packages"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--af-lime)] hover:underline underline-offset-4 mb-7"
            data-testid="faq-back-link"
          >
            <ArrowLeft size={14} /> Back to packages
          </Link>
          <div className="flex items-center gap-3 mb-5">
            <HelpCircle size={22} className="text-[var(--af-lime)]" />
            <div className="font-eyebrow text-xs uppercase tracking-widest text-[var(--af-lime)]">
              Packages · FAQ
            </div>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6">
            The{" "}
            <span className="text-[var(--af-lime)]">straight answers</span> on
            packages.
          </h1>
          <p className="text-lg sm:text-xl text-[var(--af-cream)]/75 leading-relaxed max-w-2xl">
            Most of what new clients ask us in the first 20-minute call,
            answered up front. If your question isn't here, the lead form at
            the bottom of any page lands directly in our inbox.
          </p>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-24" data-testid="faq-list-section">
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => {
              const faqId = `faq-${i}`;
              return (
                <AccordionItem
                  key={faqId}
                  value={faqId}
                  className="surface px-6 border-0 rounded-2xl"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="font-display text-lg sm:text-xl text-left hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-[var(--af-cream)]/80 leading-relaxed pb-5">
                    <p>{f.a}</p>
                    <FaqFeedbackButtons
                      faqId={faqId}
                      question={f.q}
                      voted={votes[faqId]}
                      onVote={(vote) => handleVote(faqId, f.q, vote)}
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <div
            className="mt-14 surface p-7 sm:p-9 text-center"
            data-testid="faq-final-cta"
          >
            <div className="font-display text-2xl sm:text-3xl mb-3">
              Still have questions?
            </div>
            <p className="text-sm text-[var(--af-cream)]/75 max-w-md mx-auto mb-6">
              Set up a 20-minute strategy call. No pressure, no slide deck,
              just a real conversation about what you're trying to grow.
            </p>
            <a
              href="/#lead-form"
              className="btn-lime"
              data-testid="faq-final-cta-btn"
            >
              Set up the call <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>
    </SiteShell>
  );
};

export default PackagesFAQ;
